import { useSyncExternalStore } from "react";
import toast from "react-hot-toast";

import i18n from "@/i18n";
import {
  fetchJobs,
  followJob,
  type GenerateCallbacks,
  postJob,
  removeServerJob,
} from "@/utils/generation-jobs";
import { getCategoryForModule } from "../workflows/_components/workflow-types";

// Studio's AI generations (workflows, email templates, document templates) run
// as SERVER-side jobs: the API drives the AI and buffers every frame, the
// browser only follows. Leaving the page — or reloading it — never stops a
// run; `resumeStudioJobs` lists the user's jobs and re-attaches to each from
// its first frame. Plain functions over a module-level store (not hooks),
// because a run outlives every component that could have owned it.
//
// One job per target, keyed `<module>:<target>`: a workflow category, or the
// template id an email / document generation writes into.

export type StudioJobModule = "workflows" | "email" | "document";

export interface StudioJob {
  key: string;
  module: StudioJobModule;
  /** Workflow category, or the template id (as a string). */
  target: string;
  jobId: string | null;
  /**
   * `done` = finished and not yet taken by its page. `error` = failed: kept
   * server-side (it never expires) until rebuilt or dismissed.
   */
  phase: "generating" | "done" | "error";
  prompt: string;
  startedAt: number;
  currentStep: string;
  liveLabel: string;
  message: string;
  /** Email / document: the content was written into the template draft. */
  saveAsDraft: boolean;
  result: Record<string, unknown> | null;
  abort: () => void;
}

const JOBS_PATH: Record<StudioJobModule, string> = {
  workflows: "/workflows/ai/jobs",
  email: "/company/email-templates/ai/jobs",
  document: "/company/document-templates/ai/jobs",
};

const t = (key: string, opts?: Record<string, unknown>) =>
  i18n.t(key, opts) as string;

export const studioJobKey = (module: StudioJobModule, target: string | number) =>
  `${module}:${target}`;

// ── Store ─────────────────────────────────────────────────────────────────────

const jobs = new Map<string, StudioJob>();
const listeners = new Set<() => void>();
let snapshot: StudioJob[] = [];

const emit = () => {
  snapshot = [...jobs.values()];
  listeners.forEach((l) => l());
};
const subscribe = (l: () => void) => {
  listeners.add(l);
  return () => listeners.delete(l);
};

export const getStudioJob = (key: string) => jobs.get(key) ?? null;

const setStudioJob = (key: string, job: StudioJob | null) => {
  if (job) jobs.set(key, job);
  else jobs.delete(key);
  emit();
};

const patchStudioJob = (key: string, patch: Partial<StudioJob>) => {
  const job = jobs.get(key);
  if (!job) return;
  jobs.set(key, { ...job, ...patch });
  emit();
};

export const useStudioJob = (key: string) =>
  useSyncExternalStore(
    subscribe,
    () => jobs.get(key) ?? null,
    () => null,
  );

export const useStudioJobs = () =>
  useSyncExternalStore(
    subscribe,
    () => snapshot,
    () => snapshot,
  );

/**
 * The page has used the result (navigated to it, applied it), or the user
 * cancelled: stop the run if any, forget it here and server-side.
 */
export const takeStudioJob = (key: string) => {
  const job = jobs.get(key);
  if (!job) return;
  job.abort();
  setStudioJob(key, null);
};

// ── Who is watching ───────────────────────────────────────────────────────────
// A run that ends while nobody is on its page reports by toast instead of on
// screen. Pages register on mount — independently of the job, because after a
// reload the job is mirrored only once the resume hears back.

const watchers = new Map<string, number>();

/** Register a page as watching a job key; returns the cleanup. */
export const watchStudioJob = (key: string) => {
  watchers.set(key, (watchers.get(key) ?? 0) + 1);
  return () => {
    const left = (watchers.get(key) ?? 1) - 1;
    if (left > 0) watchers.set(key, left);
    else watchers.delete(key);
  };
};

const isWatched = (key: string) => watchers.has(key);

// ── Runner ────────────────────────────────────────────────────────────────────

/** Toast when a result lands with nobody on its page. */
const readyToast = (job: StudioJob) => {
  const name = String(job.result?.name ?? "");
  if (job.module === "workflows")
    return toast.success(t("studio:jobs.workflow_ready", { name }));
  return toast.success(
    job.module === "email"
      ? t("studio:jobs.email_ready")
      : t("studio:jobs.document_ready"),
  );
};

const callbacksFor = (key: string, jobId: string): GenerateCallbacks<Record<string, unknown>> => {
  const mine = () => getStudioJob(key)?.jobId === jobId;

  // Failures are kept (server-side, for good) so they can be rebuilt: stay
  // listed here, on the page if it is open and in the dock otherwise.
  const fail = (message: string) => {
    if (!mine()) return;
    patchStudioJob(key, { phase: "error", message });
    if (!isWatched(key)) toast.error(message);
  };

  return {
    onStatus: (step, label) => {
      if (!mine()) return;
      patchStudioJob(key, {
        ...(step ? { currentStep: step } : {}),
        liveLabel: label,
      });
    },
    onFinal: (result) => {
      const job = getStudioJob(key);
      if (!job || job.jobId !== jobId) return;
      const ok =
        !!result.success &&
        (job.module === "workflows" ? !!result.template_id : !!result.contentJson);
      if (!ok) {
        fail(String(result.message || t("studio:jobs.generation_failed")));
        return;
      }
      patchStudioJob(key, { phase: "done", result });
      if (isWatched(key)) return; // the page takes it
      // Nobody watching. A saved result (a workflow is always saved as a draft;
      // email/document when asked) is already in its list — say so and forget
      // it. An unsaved one waits for its builder to apply it.
      if (job.module === "workflows" || result.saved) {
        readyToast({ ...job, result });
        takeStudioJob(key);
      } else {
        toast.success(t("studio:jobs.content_ready_open_builder"));
      }
    },
    onError: (err) =>
      fail(err.message || t("studio:jobs.generation_failed")),
    onDone: () => {},
  };
};

const initial = (
  key: string,
  module: StudioJobModule,
  target: string,
  prompt: string,
  saveAsDraft: boolean,
  controller: AbortController,
  jobId: string | null,
  startedAt: number,
): StudioJob => ({
  key,
  module,
  target,
  jobId,
  phase: "generating",
  prompt,
  startedAt,
  currentStep: "",
  liveLabel: "",
  message: "",
  saveAsDraft,
  result: null,
  abort: () => {
    controller.abort();
    removeServerJob(JOBS_PATH[module], getStudioJob(key)?.jobId ?? jobId);
  },
});

export interface StartStudioJob {
  module: StudioJobModule;
  target: string | number;
  prompt: string;
  /** The module's start route (POST). */
  startPath: string;
  body: unknown;
  saveAsDraft?: boolean;
}

/** Start a generation as a server job and follow it. */
export const startStudioJob = async (input: StartStudioJob) => {
  const target = String(input.target);
  const key = studioJobKey(input.module, target);
  // A new run for the same target replaces the old one.
  takeStudioJob(key);
  const controller = new AbortController();
  setStudioJob(
    key,
    initial(
      key,
      input.module,
      target,
      input.prompt,
      !!input.saveAsDraft,
      controller,
      null,
      Date.now(),
    ),
  );

  let jobId: string;
  try {
    jobId = await postJob(input.startPath, input.body);
  } catch (err) {
    if (!getStudioJob(key)) return;
    patchStudioJob(key, {
      phase: "error",
      message: (err as Error).message || t("studio:jobs.generation_failed"),
    });
    return;
  }
  // Cancelled while the job was being created.
  if (!getStudioJob(key) || controller.signal.aborted) {
    removeServerJob(JOBS_PATH[input.module], jobId);
    return;
  }
  patchStudioJob(key, { jobId });
  followJob(JOBS_PATH[input.module], jobId, callbacksFor(key, jobId), controller);
};

// ── Resume after a reload ─────────────────────────────────────────────────────

interface ServerJob {
  id: string;
  status: "running" | "finished" | "failed";
  /** Failed jobs only. */
  error?: { message: string; code: string | null };
  createdAt: number;
  prompt?: string;
  /** Workflows. */
  module?: string;
  /** Email / document. */
  templateId?: number;
  saveAsDraft?: boolean;
}

const resumed = new Set<StudioJobModule>();

/**
 * Re-attach, once per page load per module, to the runs the server is still
 * holding for this user. Each stream replays from frame 0, so progress and a
 * finished result come back exactly. A job already mirrored here is left
 * alone. Modules the user cannot see answer 403 and are skipped.
 */
export const resumeStudioJobs = async (modules: StudioJobModule[]) => {
  await Promise.all(
    modules.map(async (module) => {
      if (resumed.has(module)) return;
      resumed.add(module);
      let list: ServerJob[];
      try {
        list = await fetchJobs<ServerJob>(JOBS_PATH[module]);
      } catch {
        resumed.delete(module); // try again on the next page that asks
        return;
      }
      for (const job of list) {
        const target =
          module === "workflows"
            ? getCategoryForModule(job.module ?? "")
            : String(job.templateId ?? "");
        if (!target) continue;
        const key = studioJobKey(module, target);
        if (getStudioJob(key)) continue;
        const controller = new AbortController();
        setStudioJob(
          key,
          initial(
            key,
            module,
            target,
            job.prompt ?? "",
            !!job.saveAsDraft,
            controller,
            job.id,
            job.createdAt,
          ),
        );
        if (job.status === "failed") {
          // A kept failure: list it for rebuild — no replay, no toast.
          patchStudioJob(key, {
            phase: "error",
            message: job.error?.message ?? t("studio:jobs.generation_failed"),
          });
          continue;
        }
        followJob(JOBS_PATH[module], job.id, callbacksFor(key, job.id), controller);
      }
    }),
  );
};

// ── Rebuild ───────────────────────────────────────────────────────────────────

/**
 * Re-run a failed generation from the request the server kept, as a new job
 * followed under the same key (so its page / the dock pick it up).
 */
export const rebuildStudioJob = async (key: string) => {
  const job = getStudioJob(key);
  if (!job?.jobId || job.phase !== "error") return;
  const failedId = job.jobId;
  const controller = new AbortController();
  setStudioJob(key, {
    ...initial(
      key,
      job.module,
      job.target,
      job.prompt,
      job.saveAsDraft,
      controller,
      null,
      Date.now(),
    ),
  });
  let jobId: string;
  try {
    jobId = await postJob(`${JOBS_PATH[job.module]}/${failedId}/retry`, {});
  } catch (err) {
    setStudioJob(key, {
      ...job,
      message: (err as Error).message || job.message,
    });
    toast.error((err as Error).message || t("studio:jobs.generation_failed"));
    return;
  }
  if (!getStudioJob(key) || controller.signal.aborted) {
    removeServerJob(JOBS_PATH[job.module], jobId);
    return;
  }
  patchStudioJob(key, { jobId });
  followJob(JOBS_PATH[job.module], jobId, callbacksFor(key, jobId), controller);
};
